import { useEffect, useState } from "react";
import { Loader2, Users } from "lucide-react";
import { toast } from "sonner";
import { errorMessage, get, put } from "@/lib/api";
import { WORKER_ROLE_OPTIONS } from "@/lib/constants";
import { useAuth } from "@/context/AuthContext";
import { Switch } from "@/components/ui/switch";

export const UserTypesAdmin = () => {
  const { refreshProfile } = useAuth();
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    get("/platform/user-types").then(setS).catch((e) => toast.error(errorMessage(e)));
  }, []);

  const toggle = async (role, label, on) => {
    setBusy(role);
    try {
      const disabled = on ? s.disabled.filter((r) => r !== role) : [...s.disabled, role];
      setS(await put("/platform/user-types", { disabled }));
      toast.success(`${label} ${on ? "turned on" : "turned off"} for every company`);
      refreshProfile();
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setBusy(null);
    }
  };

  if (!s) return <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;
  return (
    <section className="max-w-2xl space-y-4 rounded-xl border border-slate-200 bg-white p-6 shadow-sm" data-testid="user-types-admin">
      <div className="flex gap-3">
        <Users className="mt-0.5 h-5 w-5 shrink-0 text-blue-700" />
        <div>
          <h2 className="font-display text-lg font-bold text-slate-900">Account types</h2>
          <p className="mt-1 text-sm text-slate-600">Turning a type off hides it for every company: it can't be invited, chosen on runs or turned on in a company&apos;s settings. People and past runs are kept, and come back when you turn it on again. Locksmiths are always on.</p>
        </div>
      </div>
      <ul className="divide-y divide-slate-100">
        {WORKER_ROLE_OPTIONS.filter(([role]) => s.disableable.includes(role)).map(([role, label]) => {
          const on = !s.disabled.includes(role);
          return (
            <li key={role} className="flex items-center justify-between gap-3 py-3" data-testid={`user-type-row-${role}`}>
              <span>
                <span className="block font-medium text-slate-900">{label}</span>
                <span className={`text-xs font-medium ${on ? "text-emerald-700" : "text-rose-700"}`} data-testid={`user-type-status-${role}`}>{on ? "On" : "Off for every company"}</span>
              </span>
              <span className="flex items-center gap-2">
                {busy === role && <Loader2 className="h-4 w-4 animate-spin text-slate-400" />}
                <Switch checked={on} disabled={!!busy} onCheckedChange={(v) => toggle(role, label, v)} aria-label={`${label} sitewide`} data-testid={`user-type-switch-${role}`} />
              </span>
            </li>
          );
        })}
      </ul>
    </section>
  );
};
